import { Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingSocialIcons from '../../components/base/FloatingSocialIcons';

const ConsultationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    preferredDate: '',
    preferredTime: '',
    consultationType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4bu73moquaeac4hdva0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          businessName: '',
          preferredDate: '',
          preferredTime: '',
          consultationType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 relative overflow-hidden">
      <FloatingSocialIcons />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Free Consultation
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Let's discuss your social media goals! Book a free 30-minute consultation with our experts. No obligation, no pressure - just valuable insights for your business.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              What You'll Get
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-full mx-auto mb-4">
                  <i className="ri-search-eye-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">Social Media Audit</h3>
                <p className="text-gray-600">We'll review your current social media presence and identify opportunities for growth</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-full mx-auto mb-4">
                  <i className="ri-lightbulb-flash-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">Custom Strategy</h3>
                <p className="text-gray-600">Get personalized recommendations tailored to your business goals and target audience</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-full mx-auto mb-4">
                  <i className="ri-question-answer-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">Expert Answers</h3>
                <p className="text-gray-600">Ask anything about social media marketing and get honest, actionable advice</p>
              </div>
            </div>
          </div>
        </section>

        {/* Booking Form */}
        <section className="py-16 px-6 bg-white/60 backdrop-blur-sm">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-2xl">
              <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
                Book Your Free Consultation
              </h2>
              <form id="consultation-form" onSubmit={handleSubmit} data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="businessName" className="block text-gray-700 font-semibold mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                      placeholder="Your Business Name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="preferredDate" className="block text-gray-700 font-semibold mb-2">
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="preferredTime" className="block text-gray-700 font-semibold mb-2">
                      Preferred Time *
                    </label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 pr-8 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Select time</option>
                      <option value="9:00 AM">9:00 AM</option>
                      <option value="10:00 AM">10:00 AM</option>
                      <option value="11:00 AM">11:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="1:00 PM">1:00 PM</option>
                      <option value="2:00 PM">2:00 PM</option>
                      <option value="3:00 PM">3:00 PM</option>
                      <option value="4:00 PM">4:00 PM</option>
                      <option value="5:00 PM">5:00 PM</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="consultationType" className="block text-gray-700 font-semibold mb-2">
                    Consultation Type *
                  </label>
                  <select
                    id="consultationType"
                    name="consultationType"
                    value={formData.consultationType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 pr-8 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select consultation type</option>
                    <option value="phone">Phone Call</option>
                    <option value="video">Video Call (Zoom)</option>
                    <option value="inperson">In-Person Meeting</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                    Tell us about your business and goals
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm resize-none"
                    placeholder="What would you like to discuss during the consultation?"
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl">
                    <i className="ri-check-line mr-2"></i>
                    Success! We've received your consultation request. We'll contact you within 24 hours to confirm your appointment.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-xl">
                    <i className="ri-error-warning-line mr-2"></i>
                    Oops! Something went wrong. Please try again or call us directly at (555) 123-4567.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Booking...' : 'Book Free Consultation'}
                </button>

                <p className="text-center text-sm text-gray-600 mt-4">
                  <i className="ri-shield-check-line mr-1"></i>
                  100% Free • No Obligation • No Credit Card Required
                </p>
              </form>
            </div>
          </div>
        </section>

        {/* Guarantee Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-3xl p-12 text-center shadow-2xl">
            <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
              <i className="ri-shield-star-fill text-5xl text-white"></i>
            </div>
            <h2 className="text-4xl font-bold text-white mb-6">
              Our 100% Satisfaction Guarantee
            </h2>
            <p className="text-xl text-white/90 mb-8">
              We're so confident in our services that we offer a 100% satisfaction guarantee. If you're not completely happy with our work in the first 30 days, we'll make it right or provide a full refund. That's our promise to you.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
                <i className="ri-customer-service-2-fill text-4xl text-white mb-3"></i>
                <div className="font-bold text-white text-lg">Dedicated Support</div>
                <div className="text-white/80 text-sm">Always here when you need us</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
                <i className="ri-trophy-fill text-4xl text-white mb-3"></i>
                <div className="font-bold text-white text-lg">Proven Results</div>
                <div className="text-white/80 text-sm">150+ successful clients</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
                <i className="ri-money-dollar-circle-fill text-4xl text-white mb-3"></i>
                <div className="font-bold text-white text-lg">Affordable Pricing</div>
                <div className="text-white/80 text-sm">Plans for every budget</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ConsultationPage;